import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
  EmailTemplate,
  EmailTemplateCreateRequest,
  EmailTemplateUpdateRequest,
  SendTestEmailRequest,
  EmailTemplateListResponse,
  EmailTemplateResponse,
  EmailTemplateDeleteResponse
} from '../models/emailTemplate.model';

@Injectable({
  providedIn: 'root',
})
export class EmailTemplateService {
  private _allTemplates: BehaviorSubject<EmailTemplateListResponse | null> = new BehaviorSubject(null);

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get allTemplates$(): Observable<EmailTemplateListResponse> {
    return this._allTemplates.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all email templates with pagination, sorting, and search
   */
  getAll(
    page: number = 1,
    limit: number = 10,
    isActive?: boolean,
    search: string = ''
  ): Observable<EmailTemplateListResponse> {
    const params: any = {
      page,
      limit,
      search,
    };

    if (isActive !== undefined && isActive !== null) {
      params.isActive = isActive;
    }

    return this._httpClient
      .get<EmailTemplateListResponse>(environment.apiURL + 'admin/email-templates', { params })
      .pipe(
        tap((response) => {
          this._allTemplates.next(response);
        })
      );
  }

  /**
   * Get single email template by ID
   */
  getById(id: string): Observable<EmailTemplateResponse> {
    return this._httpClient.get<EmailTemplateResponse>(
      environment.apiURL + 'admin/email-templates/' + id
    );
  }

  /**
   * Create new email template
   */
  create(data: EmailTemplateCreateRequest): Observable<EmailTemplateResponse> {
    return this.allTemplates$.pipe(
      take(1),
      switchMap((templates) =>
        this._httpClient
          .post<EmailTemplateResponse>(environment.apiURL + 'admin/email-templates', data)
          .pipe(
            map((response) => {
              if (templates && templates.templates) {
                templates.templates.unshift(response.template);
                templates.total += 1;
                this._allTemplates.next(templates);
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Update email template
   */
  update(id: string, data: EmailTemplateUpdateRequest): Observable<EmailTemplateResponse> {
    return this.allTemplates$.pipe(
      take(1),
      switchMap((templates) =>
        this._httpClient
          .put<EmailTemplateResponse>(environment.apiURL + 'admin/email-templates/' + id, data)
          .pipe(
            map((response) => {
              if (templates && templates.templates) {
                const index = templates.templates.findIndex((item) => item._id === id);
                if (index !== -1) {
                  templates.templates[index] = response.template;
                  this._allTemplates.next(templates);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Delete email template
   */
  delete(id: string): Observable<EmailTemplateDeleteResponse> {
    return this.allTemplates$.pipe(
      take(1),
      switchMap((templates) =>
        this._httpClient
          .delete<EmailTemplateDeleteResponse>(environment.apiURL + 'admin/email-templates/' + id)
          .pipe(
            map((response) => {
              if (templates && templates.templates) {
                const index = templates.templates.findIndex((item) => item._id === id);
                if (index !== -1) {
                  templates.templates.splice(index, 1);
                  templates.total -= 1;
                  this._allTemplates.next(templates);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Send test email
   */
  sendTestEmail(id: string, data: SendTestEmailRequest): Observable<{ success: boolean; message: string }> {
    return this._httpClient.post<{ success: boolean; message: string }>(
      environment.apiURL + 'admin/email-templates/' + id + '/test',
      data
    );
  }

  /**
   * Preview email template with test data
   * Returns rendered HTML
   */
  preview(id: string, testData: any): Observable<string> {
    return this._httpClient.post(
      environment.apiURL + 'admin/email-templates/' + id + '/preview',
      { testData },
      { responseType: 'text' }
    );
  }
}
